"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import type { Photo } from '@/lib/photos'
import { getSelectionFavoriteToken, getSelectionShareToken, type SelectedPhoto } from '@/lib/selection-policy'

interface SelectionContextType {
  selectedPhotos: SelectedPhoto[]
  toggleSelection: (photo: Photo, options?: { shareToken?: string }) => void
  isSelected: (photoId: string) => boolean
  clearSelection: () => void
  selectionCount: number
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined)

function toSelectedPhoto(photo: Photo | SelectedPhoto, shareToken?: string): SelectedPhoto {
  const token = shareToken || getSelectionShareToken(photo)
  const favoriteToken = getSelectionFavoriteToken(photo)
  return {
    ...photo,
    ...(token ? { shareToken: token } : {}),
    ...(favoriteToken ? { favoriteToken } : {}),
  }
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([])
  const [isHydrated, setIsHydrated] = useState(false)

  // Hydrate from localStorage after mount (avoids SSR/client mismatch)
  useEffect(() => {
    const saved = localStorage.getItem('photo-selection')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) {
          setSelectedPhotos(parsed.map(photo => toSelectedPhoto(photo as SelectedPhoto)))
        }
      } catch (e) {
        console.error('Failed to parse selection from local storage', e)
      }
    }
    setIsHydrated(true)
  }, [])

  // Persist to localStorage on change (only after initial hydration)
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('photo-selection', JSON.stringify(selectedPhotos))
    }
  }, [selectedPhotos, isHydrated])

  const toggleSelection = (photo: Photo, options: { shareToken?: string } = {}) => {
    setSelectedPhotos(prev => {
      const exists = prev.some(p => p.id === photo.id)
      if (exists) {
        return prev.filter(p => p.id !== photo.id)
      } else {
        return [...prev, toSelectedPhoto(photo, options.shareToken)]
      }
    })
  }

  const isSelected = (photoId: string) => {
    return selectedPhotos.some(p => p.id === photoId)
  }

  const clearSelection = () => {
    setSelectedPhotos([])
  }

  return (
    <SelectionContext.Provider value={{
      selectedPhotos,
      toggleSelection,
      isSelected,
      clearSelection,
      selectionCount: selectedPhotos.length
    }}>
      {children}
    </SelectionContext.Provider>
  )
}

export function useSelection() {
  const context = useContext(SelectionContext)
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider')
  }
  return context
}
